/**
 * CampaignQuickLaunchLightbox.jsx
 * ──────────────────────────────────────────────────────────────
 * 580px modal triggered from a segment's "Start campaign" action.
 * Lets the user spin up a call campaign in 3 fields:
 *   • Campaign name (AI-suggests from the segment)
 *   • Script assignment (preview + AI-suggest)
 *   • Agent assignment — round-robin OR shared pool
 *
 * Pure frontend; on Launch we navigate to /dialer with query
 * params so the Sales Dialer can pre-load the campaign.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, X, Play, FileText, Users, ChevronRight, Loader2, SlidersHorizontal, RotateCcw } from "lucide-react";
import { API, useAuth } from "@/App";
import { LeadFilterBody, EMPTY_LEAD_FILTERS, countActiveFilters, applyLeadFilters } from "@/components/LeadFilterPanel";

/* Demo script library — mirrors SalesDialer.jsx::DEMO_SCRIPTS shape */
const DEMO_SCRIPT_LIBRARY = [
  { id: "scr-ccm-intro", name: "CCM Enrollment Intro", specialty: ["Family Medicine", "Internal Medicine", "Geriatric Medicine"], product: "CCM", preview: "Hi Dr. {lastName}, calling about a Medicare-reimbursable chronic-care program for your high-utilization patients. Most practices with 500+ benes recover the setup cost in 2 months…" },
  { id: "scr-rpm-cardio", name: "RPM Cardiology Wedge", specialty: ["Cardiology"], product: "RPM", preview: "Hi Dr. {lastName}, calling about HCPCS 99453/99454 — remote BP / weight monitoring is reimbursable for your HTN + CHF panel. Practices your size add ~$28K/yr…" },
  { id: "scr-apcm-pcp", name: "APCM Primary Care Pitch", specialty: ["Family Medicine", "Internal Medicine"], product: "APCM", preview: "Hi Dr. {lastName}, 2026 APCM codes G0556/G0557/G0558 replace the old TCM/CCM stack — bigger bundle, less paperwork. Want a 90-second walkthrough?" },
  { id: "scr-ehr-migration", name: "EHR Migration — Sunset Vendors", specialty: ["all"], product: "EHR", preview: "Hi Dr. {lastName}, noticed your EHR is on a sunsetting roadmap. We're moving 40+ practices off this quarter — 47-day average go-live. Got 90 seconds?" },
  { id: "scr-awv-geri", name: "AWV — Geriatric Heavy Practices", specialty: ["Family Medicine", "Geriatric Medicine"], product: "AWV", preview: "Hi Dr. {lastName}, your panel skews 65+. AWV (G0438/G0439) is paying about $172/visit and most practices miss 60% of eligible patients. Worth 5 minutes?" },
  { id: "scr-generic-intro", name: "Generic First-Touch Intro", specialty: ["all"], product: "any", preview: "Hi Dr. {lastName}, this is {agent} from Enable Healthcare. Got 90 seconds? I noticed your practice profile and wanted to share something specific…" },
];

// Roles eligible to be assigned as a dialing agent on a campaign.
// Broadened (Iter 142) to include every role that can be on a phone:
// SDR/demo/sales/bookings/call-agent reps, plus managers/admins (they
// often help cover a queue). `read_only` is intentionally excluded.
const SALES_ROLES = new Set([
  "sales_agent", "sales_manager", "senior_sales", "demo_agent", "sdr",
  "call_agent", "bookings_agent", "marketing_manager", "marketing_agent",
  "crm_admin", "super_admin", "admin", "tenant_admin", "manager",
  "executive", "ceo",
]);

function formatRoleLabel(role) {
  if (!role) return "Sales agent";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* AI-suggest a campaign name from segment metadata */
function trimToWord(s, n) {
  if (!s || s.length <= n) return s || "";
  const sliced = s.slice(0, n);
  const lastSpace = sliced.lastIndexOf(" ");
  if (lastSpace > n * 0.6) return sliced.slice(0, lastSpace).replace(/[—·,\-]\s*$/, "").trim();
  return sliced.trim();
}
function suggestCampaignName(segment) {
  if (!segment) return "New campaign";
  if (segment.primary && segment.tagline) return trimToWord(`${segment.primary} — ${segment.tagline}`, 64);
  if (segment.name) {
    // turn "CCM Targets — Internal Medicine" into "CCM Outreach · Internal Medicine"
    return trimToWord(segment.name.replace(/Targets|Segment|Leads/gi, "Outreach"), 64);
  }
  return "New campaign";
}

/* AI-suggest a script from segment.primary product + specialty */
function suggestScript(segment) {
  if (!segment) return DEMO_SCRIPT_LIBRARY[5];
  const primary = (segment.primary || "").toUpperCase();
  // Match by primary product first
  const byProduct = DEMO_SCRIPT_LIBRARY.find((s) => s.product === primary);
  if (byProduct) return byProduct;
  // Fallback by specialty hint in segment.conditions / name
  const specHint = (segment.specialty || segment.name || "").toLowerCase();
  if (/cardio/.test(specHint)) return DEMO_SCRIPT_LIBRARY.find((s) => s.id === "scr-rpm-cardio");
  if (/geri/.test(specHint)) return DEMO_SCRIPT_LIBRARY.find((s) => s.id === "scr-awv-geri");
  if (/primary|family|internal/.test(specHint)) return DEMO_SCRIPT_LIBRARY.find((s) => s.id === "scr-apcm-pcp");
  return DEMO_SCRIPT_LIBRARY[5];
}

export default function CampaignQuickLaunchLightbox({ open, segment, leadCount, segmentLeads, onClose, onLaunch }) {
  const { token } = useAuth();
  const aiName = useMemo(() => suggestCampaignName(segment), [segment]);
  const aiScript = useMemo(() => suggestScript(segment), [segment]);

  const [name, setName] = useState(aiName);
  const [scriptId, setScriptId] = useState(aiScript?.id || DEMO_SCRIPT_LIBRARY[0].id);
  const [assignMode, setAssignMode] = useState("round_robin"); // round_robin | shared_pool
  const [agents, setAgents] = useState([]); // live users from /api/users/directory
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [launching, setLaunching] = useState(false);

  // ─── Audience filters (Feb 2026) ─────────────────────────────────
  // Mirror the Lead Focus / Leads page filter set so reps can narrow
  // the campaign audience by ALL the same dimensions (CMS / Medicare
  // beneficiaries / specialty / EHR / etc.) before launch.
  const [filters, setFilters] = useState(EMPTY_LEAD_FILTERS);
  const [extraFilters, setExtraFilters] = useState({ state: [], city: [], zip: [] });
  const [audienceLeads, setAudienceLeads] = useState([]); // raw lead pool
  const [audienceLoading, setAudienceLoading] = useState(false);

  // Iter 140.6 (user-reported "5,000 leads bug") — when the parent
  // (LeadSegments) hands us the segment's already-filtered match
  // list, use THAT as the audience pool instead of fetching the
  // global 5,000-lead universe. This was the actual root cause of
  // every quick-launch campaign coming out with exactly 5,000 leads,
  // no script, no filters: the lightbox was treating ALL leads as
  // the segment audience and shipping the full pool to the dialer.
  useEffect(() => {
    if (!open || !token) return;
    if (Array.isArray(segmentLeads) && segmentLeads.length > 0) {
      // Parent passed the segment's filtered matches — use directly.
      setAudienceLeads(segmentLeads);
      setAudienceLoading(false);
      return undefined;
    }
    // Fallback: parent didn't pass segmentLeads (older callers) — fall
    // back to a global fetch capped at 5,000.
    let cancelled = false;
    setAudienceLoading(true);
    axios.get(`${API}/leads?limit=5000`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const leads = Array.isArray(res.data) ? res.data
                    : Array.isArray(res.data?.leads) ? res.data.leads
                    : [];
        setAudienceLeads(leads);
      })
      .catch(() => { if (!cancelled) setAudienceLeads([]); })
      .finally(() => { if (!cancelled) setAudienceLoading(false); });
    return () => { cancelled = true; };
  }, [open, token, segmentLeads]);

  const filteredLeads = useMemo(() => {
    if (!audienceLeads.length) return [];
    let pool = applyLeadFilters(audienceLeads, filters);
    if (extraFilters.state.length)
      pool = pool.filter((l) => extraFilters.state.includes((l.state || "").toUpperCase()));
    if (extraFilters.city.length)
      pool = pool.filter((l) => extraFilters.city.some((c) => (l.city || "").toLowerCase().includes(c.toLowerCase())));
    if (extraFilters.zip.length)
      pool = pool.filter((l) => extraFilters.zip.some((z) => String(l.zip || l.zip_code || "").startsWith(z)));
    return pool;
  }, [audienceLeads, filters, extraFilters]);

  const filterCount = useMemo(
    () => countActiveFilters(filters) + extraFilters.state.length + extraFilters.city.length + extraFilters.zip.length,
    [filters, extraFilters],
  );

  const clearFilters = () => {
    setFilters({ ...EMPTY_LEAD_FILTERS });
    setExtraFilters({ state: [], city: [], zip: [] });
  };

  // Resolved audience size: filter result if filters are active, else
  // fall back to the prop count provided by the caller.
  const effectiveLeadCount = filterCount > 0 ? filteredLeads.length : (leadCount ?? audienceLeads.length);

  // Fetch live agent directory once when the modal opens. Filters down
  // to sales-facing roles only; falls back to "all users" if no role
  // tagged correctly (avoids an empty picker on a fresh workspace).
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setAgentsLoading(true);
    axios.get(`${API}/users/directory`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const allUsers = Array.isArray(res.data?.users) ? res.data.users : [];
        const salesUsers = allUsers.filter((u) => SALES_ROLES.has((u.role || "").toLowerCase()));
        const list = (salesUsers.length > 0 ? salesUsers : allUsers).map((u) => ({
          id: u.id,
          name: u.full_name || u.email || "Unnamed",
          role: formatRoleLabel(u.role),
          email: u.email,
        }));
        setAgents(list);
        // Default-select up to 3 to keep the launch path one click long.
        setSelectedAgents(list.slice(0, 3).map((a) => a.id));
      })
      .catch(() => { if (!cancelled) setAgents([]); })
      .finally(() => { if (!cancelled) setAgentsLoading(false); });
    return () => { cancelled = true; };
  }, [open, token]);

  // Re-sync the AI fields whenever the modal opens with a different segment
  useEffect(() => {
    if (open) {
      setName(aiName);
      setScriptId(aiScript?.id || DEMO_SCRIPT_LIBRARY[0].id);
      setAssignMode("round_robin");
      setLaunching(false);
    }
  }, [open, aiName, aiScript]);

  if (!open) return null;

  const selectedScript = DEMO_SCRIPT_LIBRARY.find((s) => s.id === scriptId) || DEMO_SCRIPT_LIBRARY[0];

  const toggleAgent = (id) => {
    setSelectedAgents((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const launch = () => {
    if (!name.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    if (selectedAgents.length === 0) {
      toast.error("Assign at least one agent");
      return;
    }
    setLaunching(true);
    setTimeout(() => {
      // ── Iter 138 (user-reported): "Start campaign" was creating
      //   campaigns with 0 leads in the dialer because leadIds was
      //   only sent when filterCount > 0 (i.e. when the rep applied
      //   extra filters inside the modal). When the rep just hit
      //   Launch without tweaking filters, the backend received
      //   `leadIds: null` and had no way to seed the queue — so the
      //   campaign card showed 0 leads even though the segment said
      //   2,708. Always send the effective lead-id list (filtered
      //   when filters are active, else the full segment audience).
      const effectiveIds = (filterCount > 0 ? filteredLeads : audienceLeads)
        .map((l) => l?.id)
        .filter(Boolean);
      onLaunch?.({
        name: name.trim(),
        script_id: scriptId,
        script_name: selectedScript.name,
        assignMode,
        agentIds: selectedAgents,
        segmentId: segment?.id,
        segmentName: segment?.name,
        leadCount: effectiveLeadCount,
        filters: filterCount > 0 ? filters : null,
        extraFilters: filterCount > 0 ? extraFilters : null,
        leadIds: effectiveIds,
      });
      setLaunching(false);
    }, 350);
  };

  return (
    <div
      data-testid="campaign-launch-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.42)", backdropFilter: "blur(6px)",
        // Iter 138 — bumped from 9000 → 10500 because the shared
        // bottom-dock container in App.js sits at z-9000 (hosts the AI
        // chat FAB, NotificationBell, and the GlobalWebPhoneDock pill).
        // Same z-index + later DOM position made the dock visually
        // cover the bottom-right of this modal (Share-with-team
        // checkbox, Launch buttons). Bumping above 9000 lets the modal
        // win the stack.
        zIndex: 10500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        data-testid="campaign-launch-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1040px, 96vw)",
          height: "min(820px, 92vh)",
          background: "#fff", borderRadius: 14, boxShadow: "0 24px 72px -12px rgba(15,23,42,0.32)",
          fontFamily: "inherit",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 22px 14px", borderBottom: "0.5px solid #eeeeee", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#5B6CFF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
              Quick-launch campaign
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--navy, #0F1E3D)", letterSpacing: "-0.01em" }}>
              {segment?.name || "New campaign"}
            </h2>
            <div style={{ marginTop: 4, fontSize: 11, color: "#888" }}>
              <span data-testid="campaign-launch-lead-count">
                {audienceLoading
                  ? "Loading audience…"
                  : `${effectiveLeadCount.toLocaleString()} leads in audience`}
              </span>
              {filterCount > 0 && (
                <span style={{ color: "#5B6CFF", fontWeight: 500 }}>
                  {" · "}{filterCount} filter{filterCount === 1 ? "" : "s"} active
                </span>
              )}
              {" · 4-step setup"}
            </div>
          </div>
          <button
            data-testid="campaign-launch-close"
            onClick={onClose}
            style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: "#888" }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body — split into left filter rail + right config rail */}
        <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(320px, 1fr) minmax(360px, 1.05fr)", gap: 0, overflow: "hidden" }}>
          {/* Left: Audience filters */}
          <div
            data-testid="campaign-launch-filters-pane"
            style={{
              borderRight: "0.5px solid #eeeeee",
              display: "flex", flexDirection: "column", minHeight: 0, background: "#FAFBFD",
            }}
          >
            <div style={{ padding: "10px 18px", borderBottom: "0.5px solid #eeeeee", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <SlidersHorizontal style={{ width: 13, height: 13, color: "#5B6CFF" }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0F1E3D" }}>Audience filters</span>
                {filterCount > 0 && (
                  <span data-testid="campaign-launch-filter-count" style={{ fontSize: 9, fontWeight: 700, color: "#5B6CFF", background: "#EEF2FF", padding: "2px 7px", borderRadius: 999 }}>
                    {filterCount}
                  </span>
                )}
              </div>
              {filterCount > 0 && (
                <button
                  data-testid="campaign-launch-filters-clear"
                  onClick={clearFilters}
                  style={{ background: "none", border: "none", color: "#E24B4A", fontSize: 10, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, padding: 0 }}
                >
                  <RotateCcw style={{ width: 10, height: 10 }} /> Clear
                </button>
              )}
            </div>
            <LeadFilterBody
              filters={filters}
              onChange={setFilters}
              extra={extraFilters}
              onChangeExtra={setExtraFilters}
              scrollPadding="14px 18px"
            />
          </div>

          {/* Right: Steps */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px 8px" }}>
              {/* Step 1: Campaign name */}
              <Field
                label="Campaign name"
                step={1}
                aiHint={name !== aiName ? `AI suggestion: ${aiName}` : null}
                onAiApply={name !== aiName ? () => setName(aiName) : null}
              >
                <input
                  data-testid="campaign-launch-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. CCM Outreach — Internal Medicine"
                  style={inputStyle}
                />
              </Field>

              {/* Step 2: Script */}
              <Field
                label="Script assignment"
                step={2}
                aiHint={scriptId !== aiScript?.id ? `AI suggests: ${aiScript?.name}` : `✦ AI matched · ${aiScript?.name}`}
                onAiApply={scriptId !== aiScript?.id ? () => setScriptId(aiScript?.id) : null}
              >
                <select
                  data-testid="campaign-launch-script"
                  value={scriptId}
                  onChange={(e) => setScriptId(e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer", appearance: "none", paddingRight: 32 }}
                >
                  {DEMO_SCRIPT_LIBRARY.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <div
                  data-testid="campaign-launch-script-preview"
                  style={{
                    marginTop: 8, padding: "10px 12px", background: "#F7F9FC", borderRadius: 8,
                    fontSize: 11, color: "#555", lineHeight: 1.55, border: "0.5px solid #E5E9F0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontSize: 9, fontWeight: 600, color: "#5B6CFF", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    <FileText style={{ width: 10, height: 10 }} /> Preview
                  </div>
                  {selectedScript.preview}
                </div>
              </Field>

              {/* Step 3: Audience summary (read-only, mirrors left rail) */}
              <Field label="Audience" step={3}>
                <div
                  data-testid="campaign-launch-audience-summary"
                  style={{
                    padding: "10px 12px", background: filterCount > 0 ? "#EEF2FF" : "#F7F9FC",
                    border: `0.5px solid ${filterCount > 0 ? "#CFD7FF" : "#E5E9F0"}`,
                    borderRadius: 8, fontSize: 11, color: "#444", lineHeight: 1.55,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Users style={{ width: 11, height: 11, color: "#5B6CFF" }} />
                    <strong>{effectiveLeadCount.toLocaleString()}</strong>
                    <span>lead{effectiveLeadCount === 1 ? "" : "s"} match{effectiveLeadCount === 1 ? "es" : ""}</span>
                    {filterCount > 0 && <span style={{ color: "#5B6CFF" }}>· {filterCount} filter{filterCount === 1 ? "" : "s"}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
                    {filterCount > 0
                      ? "Refine in the left rail — filter set mirrors Lead Focus."
                      : "No filters applied — full segment audience. Use the left rail to narrow."}
                  </div>
                </div>
              </Field>

              {/* Step 4: Agent assignment */}
              <Field label="Agent assignment" step={4}>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <ModeButton
                    testid="campaign-launch-mode-round-robin"
                    active={assignMode === "round_robin"}
                    onClick={() => setAssignMode("round_robin")}
                    title="Round robin"
                    subtitle="Distribute evenly · 1 lead per agent"
                  />
                  <ModeButton
                    testid="campaign-launch-mode-shared-pool"
                    active={assignMode === "shared_pool"}
                    onClick={() => setAssignMode("shared_pool")}
                    title="Shared pool"
                    subtitle="First-claim wins · race to dial"
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {agentsLoading ? (
                    <div data-testid="campaign-launch-agents-loading" style={{ gridColumn: "1 / -1", padding: 18, textAlign: "center", color: "#888", fontSize: 11 }}>
                      <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
                      Loading agents…
                    </div>
                  ) : agents.length === 0 ? (
                    <div data-testid="campaign-launch-agents-empty" style={{ gridColumn: "1 / -1", padding: 14, textAlign: "center", color: "#888", fontSize: 11, border: "0.5px dashed #E5E9F0", borderRadius: 8, background: "#FAFBFD" }}>
                      No agents in this workspace yet. Add reps via Settings → Team Members to assign campaigns.
                    </div>
                  ) : agents.map((a) => {
                    const on = selectedAgents.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        data-testid={`campaign-launch-agent-${a.id}`}
                        onClick={() => toggleAgent(a.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                          background: on ? "#EEF2FF" : "#fff",
                          border: on ? "1px solid #5B6CFF" : "0.5px solid #E5E9F0",
                          borderRadius: 8, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                        }}
                      >
                        <div style={{
                          width: 14, height: 14, borderRadius: 4,
                          border: on ? "1px solid #5B6CFF" : "1px solid #C8CFDA",
                          background: on ? "#5B6CFF" : "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          {on && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                          <div style={{ fontSize: 10, color: "#888" }}>{a.role}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 6, fontSize: 10, color: "#888", display: "flex", alignItems: "center", gap: 4 }}>
                  <Users style={{ width: 11, height: 11 }} /> {selectedAgents.length} agent{selectedAgents.length === 1 ? "" : "s"} assigned
                </div>
              </Field>
            </div>

            {/* Footer (right rail) */}
            <div style={{ padding: "12px 22px", borderTop: "0.5px solid #eeeeee", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafbfd", flexShrink: 0 }}>
              <button
                data-testid="campaign-launch-cancel"
                onClick={onClose}
                style={{
                  padding: "8px 16px", background: "#fff", border: "0.5px solid #E5E9F0",
                  borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#555", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                data-testid="campaign-launch-launch"
                onClick={launch}
                disabled={launching || effectiveLeadCount === 0}
                style={{
                  padding: "8px 16px", background: (launching || effectiveLeadCount === 0) ? "#5B6CFF99" : "#5B6CFF",
                  border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#fff",
                  cursor: (launching || effectiveLeadCount === 0) ? "not-allowed" : "pointer", fontFamily: "inherit",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  boxShadow: "0 2px 6px rgba(91,108,255,0.32)",
                }}
              >
                <Play style={{ width: 12, height: 12 }} /> {launching ? "Launching…" : `Launch · ${effectiveLeadCount.toLocaleString()}`}
                {!launching && <ChevronRight style={{ width: 12, height: 12 }} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Field helper ─────────────────────────────────────────── */
function Field({ label, step, aiHint, onAiApply, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={{ fontSize: 10, fontWeight: 600, color: "#444", letterSpacing: "0.05em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {step ? <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#EEF2FF", color: "#5B6CFF", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{step}</span> : null}
          {label}
        </label>
        {aiHint && (
          onAiApply ? (
            <button
              onClick={onAiApply}
              data-testid={`campaign-launch-${(label || "").toLowerCase().replace(/\s/g, "-")}-ai-apply`}
              style={{ background: "none", border: "none", color: "#5B6CFF", fontSize: 10, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, padding: 0, fontFamily: "inherit" }}
            >
              <Sparkles style={{ width: 10, height: 10 }} /> {aiHint}
            </button>
          ) : (
            <span style={{ fontSize: 10, color: "#5B6CFF", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Sparkles style={{ width: 10, height: 10 }} /> {aiHint}
            </span>
          )
        )}
      </div>
      {children}
    </div>
  );
}

function ModeButton({ active, onClick, title, subtitle, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        flex: 1, padding: "10px 12px",
        background: active ? "#EEF2FF" : "#fff",
        border: active ? "1px solid #5B6CFF" : "0.5px solid #E5E9F0",
        borderRadius: 8, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: active ? "#5B6CFF" : "#222" }}>{title}</div>
      <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{subtitle}</div>
    </button>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 12px",
  border: "0.5px solid #D0D5DC", borderRadius: 8,
  fontSize: 12, color: "#222", background: "#fff",
  outline: "none", fontFamily: "inherit", boxSizing: "border-box",
};
